/* eslint-disable lfds/no-raw-token-strings */
import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { scoped, declare, vars } from '#utils/styles'

export const locals = scoped('spinner', [
	'color_fill',
	'color_track',
	'single_color',
	'size',
	'internal_size',
	'background'
] as const)

export default css`
	:host {
		${declare(locals, {
			color_fill: tokens.color_border_brand_primary,
			color_track: 'var(--lfds-component-spinner-track, #E6E6E6)',
			background: 'transparent'
		})};

		display: inline-block;
		vertical-align: middle;
	}

	:host([variant='overlay']) {
		display: contents;
	}

	/*
	 * on-dark-background: explicit opt-in, or the auto default for overlay
	 * (strong backdrop) unless overridden back to on-light-background.
	 */
	:host([background-mode='on-dark-background']),
	:host([variant='overlay']:not([background-mode='on-light-background'])) {
		${declare(locals, {
			color_fill: 'var(--lfds-component-spinner-on-backdrop-strong, #F5F5F5)',
			color_track:
				'var(--lfds-component-spinner-track-on-backdrop-strong, #004B85)'
		})};

		--lfds-spinner-label-color: ${vars(tokens.color_text_on_img)};
	}

	.spinner {
		display: block;
		background: ${vars(locals.background)};
		width: calc(${vars(locals.size, locals.internal_size)} / 16 * 1rem);
		height: calc(${vars(locals.size, locals.internal_size)} / 16 * 1rem);
		user-select: none;
		animation: spinner-fade-in 180ms ease-out both;
	}

	.spinner.placeholder {
		visibility: hidden;
		animation: none;
	}

	svg {
		display: block;
		width: 100%;
		height: 100%;
		/* higher-quality anti-aliasing for the stroked circle/arc, matters most while the arc is rotating */
		shape-rendering: geometricPrecision;
	}

	.track {
		stroke: ${vars(locals.color_track)};
	}

	.arc-path {
		stroke: ${vars(locals.color_fill)};
		transform-box: view-box;
		/* must match the circle center in the path data (72x72 viewBox) */
		transform-origin: 36px 36px;
		/* both on the same element so the browser evaluates them in lockstep - no cross-element timing skew at the loop boundary */
		animation:
			spinner-rotation 1400ms linear infinite,
			spinner-arc-trim 1400ms linear infinite;
	}

	.spinner.monochrome .track,
	.spinner.monochrome .arc-path {
		stroke: ${vars(locals.single_color)};
	}

	.spinner.monochrome .track {
		stroke-opacity: 0.24;
	}

	/* inline variant */

	.root {
		display: inline-flex;
		flex-direction: column;
		align-items: center;
		gap: ${vars(tokens.spacing_sm)};
		inline-size: 9.375rem;
		min-block-size: 6.25rem;
		animation: spinner-fade-in 180ms ease-out both;
	}

	.plain-reduced {
		display: inline-flex;
		flex-direction: column;
		align-items: center;
		gap: ${vars(tokens.spacing_sm)};
	}

	.label {
		inline-size: 100%;
		font-family: ${vars(tokens.font_family_label)};
		font-style: normal;
		font-weight: ${vars(tokens.font_weight_regular)};
		text-align: center;
		color: var(--lfds-spinner-label-color, ${vars(tokens.color_text_primary)});
	}

	.label.label-sm {
		font-size: ${vars(tokens.text_size_label_sm)};
		line-height: ${vars(tokens.line_height_label_sm)};
		min-block-size: ${vars(tokens.line_height_label_sm)};
	}

	.label.label-md {
		font-size: ${vars(tokens.text_size_label_default)};
		line-height: ${vars(tokens.line_height_label_default)};
		min-block-size: ${vars(tokens.line_height_label_default)};
	}

	.label.label-lg {
		font-size: ${vars(tokens.text_size_label_lg)};
		line-height: ${vars(tokens.line_height_label_lg)};
		min-block-size: ${vars(tokens.line_height_label_lg)};
	}

	.plain-reduced .label {
		inline-size: auto;
	}

	.current-label,
	.delayed-label {
		display: block;
		white-space: pre-line;
	}

	.delayed-label {
		animation: label-fade-in 140ms ease-out both;
	}

	.live-region {
		position: absolute;
		inline-size: 1px;
		block-size: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		clip-path: inset(50%);
		white-space: nowrap;
		border: 0;
	}

	/* overlay variant */

	dialog.overlay {
		border: none;
		padding: 0;
		background: transparent;
		margin: 0;
		width: 100vw;
		max-width: 100vw;
		height: 100dvh;
		max-height: 100dvh;
	}

	dialog.overlay[open] {
		display: grid;
		place-items: center;
		animation: overlay-surface-fade-in 180ms ease-out both;
	}

	dialog.overlay::backdrop {
		background: var(
			--lfds-spinner-overlay-backdrop,
			var(--lfds-color-background-backdrop-strong, rgba(0, 22, 41, 0.8))
		);
		animation: overlay-backdrop-fade-in 180ms ease-out both;
	}

	.overlay-inner {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: ${vars(tokens.spacing_sm)};
		inline-size: 9.375rem;
		min-block-size: 6.25rem;
		animation: spinner-fade-in 180ms ease-out both;
	}

	@keyframes spinner-fade-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes label-fade-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes overlay-backdrop-fade-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes overlay-surface-fade-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes spinner-rotation {
		to {
			/* 360deg - 0.8 * 216deg (arc-path's sweep) so the dash-trim seam lines up exactly at the loop boundary */
			transform: rotate(187.2deg);
		}
	}

	@keyframes spinner-arc-trim {
		0% {
			animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
			stroke-dasharray: 0.2 1;
			stroke-dashoffset: 0;
		}
		46.429% {
			animation-timing-function: cubic-bezier(0.367, 0, 0.698, 0.357);
			stroke-dasharray: 0.7785 1;
			stroke-dashoffset: -0.2;
		}
		53.571% {
			animation-timing-function: cubic-bezier(0.429, 0.164, 0.48, 1);
			stroke-dasharray: 0.7839 1;
			stroke-dashoffset: -0.2161;
		}
		100% {
			stroke-dasharray: 0.2 1;
			stroke-dashoffset: -0.8;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.arc-path {
			animation: none;
		}

		/*
		 * freeze as a static half-ring from 1 o'clock to 7 o'clock: the base
		 * path starts at 3 o'clock and sweeps 216deg, so rotating 300deg moves
		 * its start to 1 o'clock, and showing 180/216 (~83.33%) of the path
		 * ends the visible arc at 7 o'clock.
		 */
		.arc-path {
			transform: rotate(300deg);
			stroke-dasharray: 0.8333 1;
			stroke-dashoffset: 0;
		}

		.spinner,
		.root,
		.overlay-inner,
		dialog.overlay[open],
		dialog.overlay::backdrop,
		.delayed-label {
			animation: none;
		}
	}
`
